import React, { useContext, useEffect, useRef } from "react";
import Modal from 'react-modal';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import {
  FileBrowserButtonComponent,
  FileBrowserSidebarComponent,
} from "./domains/fileBrowser/components/FileBrowserComponent";
import DBComponent from "./domains/song/services/songs-db";
import TopFloatMenuComponent from "./domains/menu/components/TopFloatMenuComponent";
import TopLeftMenuComponent from "./domains/menu/components/TopLeftMenuComponent";
import EventBridgeComponent from "./infrastructure/events/EventBridgeComponent";
import { AppCtxProvider, AppCtx } from "./AppCtx";
import createContentRoute from "./domains/content/component/ContentRouteWrapper";
import ContentViewComponent from "./domains/content/component/ContentViewComponent";
import { SearchComponent } from "./domains/song/search/components/SearchComponent";
import { ThemeContext } from "./ThemeContext";
import usePreferredColorScheme from "./shared/hooks/usePreferredColorScheme";
import {installVisibleViewport} from './shared/services/visibleViewport';
import { DashboardButtonsComponent } from "./domains/dashboard/component/DashboardComponent";
import AppWrapper from "./AppWrapperComponent";
import ConnectionComponent from "./domains/menu/components/ConnectionComponent";
import TopRightMenuComponent from "./domains/menu/components/TopRightMenuComponent";
import NaviBackForwardComponent from "./domains/menu/components/NaviBackForwardComponent";
import FullScreenButtonsComponent from "./domains/menu/components/FullScreenButtonsComponent";
import {ChangelogPage, ChangelogDetailPage} from './domains/changelog/ChangelogPages';
import {SongLibraryProvider} from './domains/song/library/SongLibraryContext';
import {SongLibraryPage} from './domains/song/library/SongLibraryPages';
import DashboardComponent from "./domains/dashboard/component/DashboardComponent";
import {RecentFilesPage, CatalogPage} from "./domains/dashboard/component/DashboardPages";
import {catalogUrl, isSongbook} from "./domains/dashboard/component/dashboardData";
import {songPathFromUrl} from "./shared/services/songPathUtils";
import BackupViewComponent from "./domains/song/backup/components/BackupViewComponent";
import { SongRouteHandler } from "./domains/navigation/components/Navigation";
import CommonSessionComponent from "./domains/menu/components/CommonSessionComponent";
import ExportButtonComponent from "./domains/song/components/ExportButtonComponents";
import FontSizeButtonsComponent from "./domains/menu/components/FontSizeButtonsComponent";
import BackupComponent from "./domains/song/backup/components/BackupComponent";
import SearchDashboardComponent from "./domains/song/search/components/SearchDashboardComponent";
import SongsViewComponent from "./domains/song/components/SongsViewComponent";
import { CollaborativeEditor } from "./domains/poc/yjs/components";
import ChordsModeButtonComponent from "./domains/menu/components/ChordsModeButtonComponent";
import YjsDebugViewComponent from "./domains/debug-view/components/YjsDebugViewComponent";
import VerticalQuickMenuWrapper, { createQuickMenuButton } from "./domains/menu/components/VerticalQuickMenuWrapper"; // Updated import
import PresentationPilotButtonComponent from "./domains/menu/components/PresentationPilotButtonComponent";
import PresentationRemoteControlPanelComponent from "./domains/song/presentation/components/PresentationRemoteControlPanelComponent";
import {SongEventHandler} from './domains/song/components/SongEventHandler';
import SongsViewSwitcherComponent from './domains/menu/components/SongsViewSwitcherComponent';
import PresentationButtonComponent from './domains/menu/components/PresentationButtonComponent';
import PresentationDeviceScreen from './domains/song/presentation/components/PresentationDeviceScreen';
import HideChordsButtonComponent from './domains/menu/components/HideChordsButtonComponent';
import ToggleSongExpansionButtonComponent from './domains/menu/components/ToggleSongExpansionButtonComponent';
import ToggleLyricSeparatorButtonComponent from './domains/menu/components/ToggleLyricSeparatorButtonComponent';
import ToggleAllSongsExpansionButtonComponent from './domains/menu/components/ToggleAllSongsExpansionButtonComponent';
import {useWorkspaceReadOnly} from './shared/hooks/useWorkspaceReadOnly';

// Import button components needed for the quick menu
import ThemeButtonComponent from "./domains/menu/components/ThemeButtonComponent";
import SearchToolsMenu from './domains/menu/components/SearchToolsMenu';

// Remove the previous configuration array
// const quickMenuItems = [ ... ];

const EventBridgeFromContext = () => {
  const { isCommonSessionEnabled, currentSessionId } = useContext(AppCtx);
  return <EventBridgeComponent isCommonSessionEnabled={isCommonSessionEnabled} currentSessionId={currentSessionId} />;
};

const WritableWorkspaceOnly = ({children}) => useWorkspaceReadOnly() ? null : children;
const WritableWorkspaceRoute = ({children}) =>
  useWorkspaceReadOnly() ? <Navigate to="/app/dashboard" replace /> : children;

const SongOrCatalogRoute = () => {
  const {pathname} = useLocation();
  const path = songPathFromUrl(pathname);
  const isFile = isSongbook(path);
  return <ContentViewComponent shrinkWithFileBrowser={!isFile}>
    {isFile ? <SongsViewComponent /> : <CatalogPage />}
  </ContentViewComponent>;
};

const LegacyCatalogRedirect = () => {
  const {search} = useLocation();
  return <Navigate to={catalogUrl(new URLSearchParams(search).get('path') || '')} replace />;
};

const App = () => {
  useEffect(installVisibleViewport, []);
  const preferredTheme = usePreferredColorScheme();
  const { setTheme } = useContext(ThemeContext);
  const appRef = useRef(null);

  useEffect(() => {
    setTheme(preferredTheme);
    Modal.setAppElement('#root');
  }, [preferredTheme, setTheme]);

  return (
    <div id="chwala-app" ref={appRef} style={{ display: "flex", width: "100%" }}>
      <DBComponent />
      <AppCtxProvider>
        <BrowserRouter>
          <SongLibraryProvider><AppWrapper appRef={appRef}>
            <EventBridgeFromContext />
            <WritableWorkspaceOnly><SongEventHandler /></WritableWorkspaceOnly>
            <FileBrowserSidebarComponent>
              <div className="sidebar-navigation-buttons context-buttons" role="group" aria-label="Nawigacja panelu bocznego">
                <FullScreenButtonsComponent/><DashboardButtonsComponent/><NaviBackForwardComponent/>
              </div>
              <div className="context-buttons">
                <CommonSessionComponent/><ThemeButtonComponent/><BackupComponent/>
                <WritableWorkspaceOnly><PresentationPilotButtonComponent/></WritableWorkspaceOnly>
                <FontSizeButtonsComponent/><ExportButtonComponent/>
                <WritableWorkspaceOnly>
                  <SongsViewSwitcherComponent/><HideChordsButtonComponent/>
                  <ToggleSongExpansionButtonComponent/><ToggleLyricSeparatorButtonComponent/>
                </WritableWorkspaceOnly>
                <PresentationButtonComponent/>
                <ChordsModeButtonComponent/><ToggleAllSongsExpansionButtonComponent/>
              </div>
            </FileBrowserSidebarComponent>
            <SongRouteHandler />
            <Routes>
              {createContentRoute({ path: "/app/backups", element: <BackupViewComponent /> })}
              {createContentRoute({ path: "/app/dashboard", element: <DashboardComponent /> })}
              {createContentRoute({ path: "/app/changelog", element: <ChangelogPage /> })}
              {createContentRoute({ path: "/app/changelog/:date/:id", element: <ChangelogDetailPage /> })}
              {createContentRoute({ path: "/app/song-library", element: <SongLibraryPage /> })}
              {createContentRoute({ path: "/app/song-favorites", element: <SongLibraryPage kind="favorites" /> })}
              {createContentRoute({ path: "/app/song-history", element: <SongLibraryPage kind="history" /> })}
              {createContentRoute({ path: "/app/recent", element: <RecentFilesPage /> })}
              <Route path="/app/catalogs" element={<LegacyCatalogRedirect />} />
              {createContentRoute({ path: "/app/search", element: <SearchDashboardComponent /> })}
              {createContentRoute({ path: "/app/collaborative-editor", element: <WritableWorkspaceRoute><CollaborativeEditor /></WritableWorkspaceRoute> })}
              {createContentRoute({ path: "/app/debug-view", element: <WritableWorkspaceRoute><YjsDebugViewComponent /></WritableWorkspaceRoute> })}
              <Route path="/songs/*" element={<SongOrCatalogRoute />} />
              <Route path="/*" element={<Navigate to="/app/dashboard" replace />} />
            </Routes>
            <TopLeftMenuComponent>
              <FileBrowserButtonComponent />
            </TopLeftMenuComponent>
            <TopFloatMenuComponent>
              <SearchComponent tools={<SearchToolsMenu/>}/>
            </TopFloatMenuComponent>
            <TopRightMenuComponent>
              <ConnectionComponent />
            </TopRightMenuComponent>
            
            {/* Define menu items as children using the helper function */}
            <SceneQuickMenu>
              <FontSizeButtonsComponent />
              {createQuickMenuButton("Zmień motyw", <ThemeButtonComponent />)}
              {createQuickMenuButton("Przełącz sesję wspólną", <CommonSessionComponent />)}
              {createQuickMenuButton("Przełącz w tryb pełnego ekranu", <FullScreenButtonsComponent />)}
              <WritableWorkspaceOnly>
                {createQuickMenuButton("Sterowanie prezentacją", <PresentationPilotButtonComponent />)}
                {createQuickMenuButton("Przełącz separator tekstu", <ToggleLyricSeparatorButtonComponent />)}
                {createQuickMenuButton("Przełącz rozwijanie pieśni", <ToggleSongExpansionButtonComponent />)}
                {createQuickMenuButton("Ukryj/Pokaż akordy", <HideChordsButtonComponent />)}
              </WritableWorkspaceOnly>
              {createQuickMenuButton("Przełącz tryb prezentacji", <PresentationButtonComponent />)}
              {createQuickMenuButton("Rozwiń/Zwiń wszystkie pieśni", <ToggleAllSongsExpansionButtonComponent />)}
            </SceneQuickMenu>

            <WritableWorkspaceOnly><PresentationRemoteControlPanelComponent /></WritableWorkspaceOnly>
            <DevicePresentation />
            
          </AppWrapper></SongLibraryProvider>
        </BrowserRouter>
      </AppCtxProvider>
    </div>
  );
};

const DevicePresentation = () => {
  const {isPresentationMode} = useContext(AppCtx);
  return process.env.REACT_APP_HOSANNA_DIRECT === '1' && isPresentationMode ? <PresentationDeviceScreen /> : null;
};

// Wrapper component for the vertical quick menu
// This is separate from the main App component to access context

export const SceneQuickMenu = ({children}) => {
  const {isTopMenuVisible, isFileBrowserVisible} = useContext(AppCtx);
  return isTopMenuVisible && isFileBrowserVisible ? null : <VerticalQuickMenuWrapper>{children}</VerticalQuickMenuWrapper>;
};

// Set the app element for the modal
Modal.setAppElement('#root');

export default App;
